import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useParams, useRouteError } from 'react-router-dom';

import { ChunkLoading, Crashed, StaleBuild } from './states';

/**
 * One error boundary per page, so a page that throws while rendering takes
 * down itself and not the toolbar — the operator can still navigate away.
 *
 * A failed chunk import (a deploy replaced the hashed file this tab was
 * about to load) is told apart, because "try again" cannot fix it and a
 * reload can.
 */
interface BoundaryState {
  error: unknown;
}

class PageBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is the only log a browser has. No content is in a render
    // error from this panel — it renders counts and metadata only.
    console.error(error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;

    if (error === null) return this.props.children;

    return isChunkError(error) ? <StaleBuild /> : <Crashed onRetry={() => this.setState({ error: null })} />;
  }
}

function isChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    `${error.name} ${error.message}`,
  );
}

/**
 * The router's own last resort (`errorElement` on the root route): what a
 * render error outside every `PageFrame` lands on — a provider or a guard.
 * Without it the data router draws its developer error page. Retrying here
 * means reloading: the tree that threw is the one that holds the session.
 */
export function RouteError() {
  const error = useRouteError();

  console.error(error);

  return isChunkError(error) ? <StaleBuild /> : <Crashed onRetry={() => window.location.reload()} />;
}

/**
 * Error boundary + Suspense around one lazily loaded page.
 *
 * `scope` is what resets the boundary. The default, the pathname, is right
 * for a leaf page. A frame with nested routes passes something coarser — the
 * workspace passes the tenant id — because keying it by pathname would
 * remount the frame, and refetch the tenant, on every tab switch.
 */
export function PageFrame({ children, scope, fallback }: { children: ReactNode; scope?: string; fallback?: ReactNode }) {
  const { pathname } = useLocation();

  return (
    <PageBoundary key={scope ?? pathname}>
      <Suspense fallback={fallback ?? <ChunkLoading />}>{children}</Suspense>
    </PageBoundary>
  );
}

/** `PageFrame` for `/tenants/:id/*`: one boundary per tenant, not per tab. */
export function WorkspaceFrame({ children }: { children: ReactNode }) {
  const { id } = useParams();

  return <PageFrame scope={`tenant:${id ?? ''}`}>{children}</PageFrame>;
}
